import React from 'react';
import get from 'lodash/get';
import SelectionCell from 'shared/ui/Table/cells/SelectionCell';
import SelectionHeader from 'shared/ui/Table/cells/SelectionHeader';
import formatNumber from 'Common/Functions/FormatNumber';
import { NameWrapper, Name } from 'Screens/Contextualization/Services/components/ServicesTable/components/NameCell/styled';
import Text from 'Screens/Contextualization/Services/components/ServicesTable/components/GenericCell/styled';
import { TagWrapper, TagStyles } from 'Screens/Contextualization/Services/components/ServicesTable/components/TagCell/styled';

const renderServiceValue = (rowInfo, attrib, { uppercase = false, numeric = false } = {}) => {
  const value = get(rowInfo, `original.${attrib}`, '');
  const rendered = numeric ? formatNumber(value) : value;

  return (
    <Text capitalize={ uppercase ? 'uppercase' : undefined }>
      {rendered}
    </Text>
  );
};

const getServiceRowId = (row) => get(row, '_id', get(row, 'id', null));
const isRowSelected = (selectedRowIds, rowId) => rowId !== null &&
  (Array.isArray(selectedRowIds) ? selectedRowIds.map(String) : []).includes(String(rowId));

// Legacy tolerated a missing `fields` (`fields ?? {}`).
const field = (fields, key) => (fields ?? {})[key];

/**
 * Column driven by `fields[fieldKey]`. Legacy didn't set `sortable`/`resizable` on these
 * columns (engine defaults) and used `defaultWidth` for the min-wd class.
 */
const serviceColumn = (fieldKey, id, column) => ({
  accessor: id,
  id,
  visible: ({ fields }) => field(fields, fieldKey)?.visible,
  maxWidth: ({ fields }) => field(fields, fieldKey)?.maxWidth,
  minWidth: ({ fields }) => field(fields, fieldKey)?.minWidth,
  order: ({ fields }) => field(fields, fieldKey)?.order,
  cellProps: (rowInfo, { fields }) => ({ className: `${id} min-wd-${field(fields, fieldKey)?.defaultWidth}` }),
  ...column
});

/**
 * "Services" tab of the Explore group view (Manage).
 * ctx: `{ fields, selectedRowIds, allSelected, onToggleRow, onToggleAll, onOpenServiceDetail }`
 */
const exploreServicesSchema = {
  id: 'exploreGroupServices',
  loading: { mockRows: 10 },
  ordering: { keepFirstColumn: false },
  columns: [
    {
      header: (headerInfo, { allSelected, onToggleAll }) => <SelectionHeader checked={ !!allSelected } onChange={ onToggleAll } />,
      accessor: 'selected',
      id: 'selected',
      maxWidth: 38,
      minWidth: 38,
      resizable: false,
      sortable: false,
      visible: true,
      order: -1,
      cell: ({ original }, { selectedRowIds, onToggleRow }) => {
        const isSelected = isRowSelected(selectedRowIds, getServiceRowId(original));
        return <SelectionCell checked={ isSelected } onChange={ () => onToggleRow(original) } />;
      }
    },
    serviceColumn('name', 'name', {
      header: () => <div>SERVICES</div>,
      cell: ({ original }, { onOpenServiceDetail }) => (
        <NameWrapper>
          <Name
            role="button"
            tabIndex={ 0 }
            onClick={ () => onOpenServiceDetail(original) }
            onKeyDown={ (e) => {
              if (e.key === 'Enter' || e.key === ' ') onOpenServiceDetail(original);
            } }
          >
            {get(original, 'name', '')}
          </Name>
        </NameWrapper>
      )
    }),
    serviceColumn('version', 'version', {
      header: () => <div>VERSION</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'version')
    }),
    serviceColumn('port', 'port', {
      header: () => <div>PORT</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'port')
    }),
    serviceColumn('protocol', 'protocol', {
      header: () => <div>PROTOCOL</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'protocol', { uppercase: true })
    }),
    serviceColumn('status', 'status', {
      header: () => <div>STATUS</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'status')
    }),
    serviceColumn('vulns', 'vulns', {
      header: () => <div>VULNS</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'vulns', { numeric: true })
    }),
    serviceColumn('tags', 'tags', {
      header: () => <div>TAGS</div>,
      visible: ({ fields, isTagsAllowedByLicense }) => field(fields, 'tags')?.visible && isTagsAllowedByLicense,
      cell: (rowInfo) => {
        const tags = get(rowInfo, 'original.tags', []);
        if (!Array.isArray(tags)) {
          return (
            <TagWrapper className="mr-2 tag">
              <TagStyles>{tags}</TagStyles>
            </TagWrapper>
          );
        }

        return (
          <>
            {tags.map((tag) => (
              <TagWrapper key={ `explore_group_service_tag_${tag}` } className="mr-2 tag">
                <TagStyles>{tag}</TagStyles>
              </TagWrapper>
            ))}
          </>
        );
      }
    }),
    serviceColumn('asset', 'parent_name', {
      header: () => <div>ASSET</div>,
      cell: (rowInfo) => renderServiceValue(rowInfo, 'parent_name')
    })
  ]
};

export default exploreServicesSchema;
